import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { getAdminReviews, toggleReviewApproval, deleteReview, getAdminLeads, deleteLead } from '../services/dbService'
import { formatTimestamp } from '../utils/helpers'
import { signInWithGoogle, logout, subscribeToAuthChanges } from '../services/authService'
import AdminProjects from '../components/AdminProjects'
import AdminUpdates from '../components/AdminUpdates'
import AdminProperties from '../components/AdminProperties'
import AdminServices from '../components/AdminServices'

const ADMIN_EMAIL = "assubuildtech@gmail.com";

export default function AdminDashboard() {
  const [user, setUser] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [authError, setAuthError] = useState('')

  const [activeTab, setActiveTab] = useState('reviews')
  const [reviews, setReviews] = useState([])
  const [leads, setLeads] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    // Listen for Firebase auth changes
    const unsubscribe = subscribeToAuthChanges((currentUser) => {
      setUser(currentUser)
      setAuthLoading(false)
    })
    
    // Cleanup subscription on unmount
    return () => unsubscribe()
  }, [])

  const handleGoogleLogin = async () => {
    setAuthError('')
    try {
      await signInWithGoogle()
    } catch (err) {
      setAuthError(err.message)
      // Attempt to sign them out just in case it persisted an unauthorized session
      await logout()
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
    } catch (err) {
      console.error("Failed to log out", err)
    }
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      if (activeTab === 'reviews') {
        const data = await getAdminReviews()
        setReviews(data)
      } else {
        const data = await getAdminLeads()
        setLeads(data)
      }
    } catch (err) {
      console.error("Error fetching data:", err)
      alert("Failed to fetch data. Check connection.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user && user.email === ADMIN_EMAIL) {
      fetchData()
    }
  }, [user, activeTab])

  const handleApprove = async (id, currentStatus) => {
    try {
      await toggleReviewApproval(id, currentStatus)
      // Optimistic update
      setReviews(reviews.map(r => r.id === id ? { ...r, approved: !currentStatus } : r))
    } catch (err) {
      console.error("Error toggling approval:", err)
      alert("Failed to update status.")
    }
  }

  const handleReviewDelete = async (id) => {
    if (window.confirm("Are you sure you want to permanently delete this Review?")) {
      try {
        await deleteReview(id)
        setReviews(reviews.filter(r => r.id !== id))
      } catch (err) {
        console.error("Error deleting review:", err)
        alert("Failed to delete.")
      }
    }
  }

  const handleLeadDelete = async (id) => {
    if (window.confirm("Are you sure you want to permanently delete this Lead?")) {
      try {
        await deleteLead(id)
        setLeads(leads.filter(l => l.id !== id))
      } catch (err) {
        console.error("Error deleting lead:", err)
        alert("Failed to delete.")
      }
    }
  }

  // SKELETON LOADER
  const SkeletonItem = () => (
    <div style={{ background: 'rgba(255,255,255,0.02)', padding: 20, borderRadius: 8, marginBottom: 16 }}>
      <div className="skeleton-box" style={{ width: '30%', height: 20, marginBottom: 15 }} />
      <div className="skeleton-box" style={{ width: '80%', height: 16, marginBottom: 8 }} />
      <div className="skeleton-box" style={{ width: '60%', height: 16, marginBottom: 20 }} />
      <div style={{ display: 'flex', gap: 10 }}>
        <div className="skeleton-box" style={{ width: 100, height: 32, borderRadius: 6 }} />
        <div className="skeleton-box" style={{ width: 100, height: 32, borderRadius: 6 }} />
      </div>
    </div>
  )

  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#060e1c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" style={{ width: 50, height: 50, border: '3px solid rgba(245,197,24,0.3)', borderTopColor: '#f5c518', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  if (!user) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#060e1c', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ background: 'rgba(255,255,255,0.05)', padding: 40, borderRadius: 16, maxWidth: 400, width: '90%', textAlign: 'center' }}>
          <i className="fas fa-user-shield fa-3x" style={{ color: '#f5c518', marginBottom: 20 }} />
          <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.5rem', marginBottom: 24 }}>Admin Login</h2>
          
          <button onClick={handleGoogleLogin} style={{ width: '100%', padding: '12px', background: 'white', color: '#111', border: 'none', borderRadius: '8px', fontSize: '1.05rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, transition: 'all 0.2s', marginBottom: '20px' }}>
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google Logo" style={{ width: 22, height: 22 }} />
            Sign in with Google
          </button>
          
          {authError && (
            <div style={{ color: '#ef4444', marginBottom: 16, fontSize: '0.9rem', padding: '10px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px' }}>
              {authError}
            </div>
          )}
          
          <div style={{ marginTop: 24 }}>
            <Link to="/" style={{ color: '#7a8fa6', textDecoration: 'none', fontSize: '0.9rem' }}><i className="fas fa-arrow-left" /> Back to Website</Link>
          </div>
        </div>
      </div>
    )
  }

  // Double check authorization
  if (user.email !== ADMIN_EMAIL) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#060e1c', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ background: 'rgba(255,255,255,0.05)', padding: 40, borderRadius: 16, borderTop: '4px solid #ef4444', textAlign: 'center', width: '100%', maxWidth: '420px' }}>
          <i className="fas fa-shield-alt" style={{ fontSize: '3rem', color: '#ef4444', marginBottom: 20 }}></i>
          <h2 style={{ fontFamily: "'Outfit', sans-serif", marginBottom: '12px' }}>Access Denied</h2>
          <p style={{ color: '#9ca3af', marginBottom: '24px', lineHeight: 1.5 }}>
            The account <strong>{user.email}</strong> is not authorized to view the admin dashboard.
          </p>
          <button onClick={handleLogout} className="btn-gold" style={{ width: '100%', padding: '12px', border: 'none', borderRadius: 8, fontWeight: 'bold' }}>
            Switch Account
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#060e1c', color: 'white', padding: '40px 24px', fontFamily: "'Inter', sans-serif" }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        
        <header style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'space-between', alignItems: 'center', marginBottom: 30, borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 20 }}>
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: '2rem', fontWeight: 800, color: '#f5c518', margin: 0 }}>ASSU Admin Panel</h1>
          
          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={fetchData} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
              <i className="fas fa-sync-alt" /> Refresh
            </button>
            <Link to="/" style={{ color: 'white', textDecoration: 'none', background: 'rgba(255,255,255,0.1)', padding: '8px 16px', borderRadius: 8, fontSize: '0.9rem' }}>
              <i className="fas fa-home" /> Site
            </Link>
            <button onClick={handleLogout} style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
              <i className="fas fa-sign-out-alt" /> Logout
            </button>
          </div>
        </header>

        <div style={{ display: 'flex', gap: 10, marginBottom: 30, overflowX: 'auto', paddingBottom: 10 }}>
          <button 
            onClick={() => setActiveTab('reviews')}
            style={{ whiteSpace: 'nowrap', padding: '10px 24px', borderRadius: 30, fontSize: '0.95rem', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.3s', background: activeTab === 'reviews' ? '#f5c518' : 'rgba(255,255,255,0.05)', color: activeTab === 'reviews' ? '#111' : 'white' }}
          >
            Client Reviews
          </button>
          <button 
            onClick={() => setActiveTab('leads')}
            style={{ whiteSpace: 'nowrap', padding: '10px 24px', borderRadius: 30, fontSize: '0.95rem', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.3s', background: activeTab === 'leads' ? '#f5c518' : 'rgba(255,255,255,0.05)', color: activeTab === 'leads' ? '#111' : 'white' }}
          >
            Leads &amp; Inquiries
          </button>
          <button 
            onClick={() => setActiveTab('projects')}
            style={{ whiteSpace: 'nowrap', padding: '10px 24px', borderRadius: 30, fontSize: '0.95rem', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.3s', background: activeTab === 'projects' ? '#f5c518' : 'rgba(255,255,255,0.05)', color: activeTab === 'projects' ? '#111' : 'white' }}
          >
            Projects
          </button>
          <button 
            onClick={() => setActiveTab('properties')}
            style={{ whiteSpace: 'nowrap', padding: '10px 24px', borderRadius: 30, fontSize: '0.95rem', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.3s', background: activeTab === 'properties' ? '#f5c518' : 'rgba(255,255,255,0.05)', color: activeTab === 'properties' ? '#111' : 'white' }}
          >
            Properties
          </button>
          <button 
            onClick={() => setActiveTab('updates')}
            style={{ whiteSpace: 'nowrap', padding: '10px 24px', borderRadius: 30, fontSize: '0.95rem', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.3s', background: activeTab === 'updates' ? '#f5c518' : 'rgba(255,255,255,0.05)', color: activeTab === 'updates' ? '#111' : 'white' }}
          >
            Company Updates
          </button>
          <button 
            onClick={() => setActiveTab('services')}
            style={{ whiteSpace: 'nowrap', padding: '10px 24px', borderRadius: 30, fontSize: '0.95rem', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.3s', background: activeTab === 'services' ? '#f5c518' : 'rgba(255,255,255,0.05)', color: activeTab === 'services' ? '#111' : 'white' }}
          >
            Services
          </button>
        </div>

        <section>
          {loading ? (
            <div>
              <SkeletonItem /><SkeletonItem /><SkeletonItem />
            </div>
          ) : activeTab === 'reviews' ? (
             <>
               <h2 style={{ fontSize: '1.2rem', marginBottom: 20, color: '#7a8fa6' }}>Manage Reviews ({reviews.length})</h2>
               {reviews.length === 0 ? (
                 <div style={{ background: 'rgba(255,255,255,0.03)', padding: 40, borderRadius: 12, textAlign: 'center', color: '#7a8fa6' }}>
                   <i className="far fa-folder-open fa-3x" style={{ opacity: 0.3, marginBottom: 16 }} />
                   <br/>No reviews found in database.
                 </div>
               ) : (
                 <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                   {reviews.map(r => (
                     <div key={r.id} style={{ 
                       background: 'rgba(255,255,255,0.04)', 
                       borderLeft: `4px solid ${r.approved ? '#22c55e' : '#ef4444'}`,
                       borderRadius: 8, padding: 20,
                       display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'space-between', alignItems: 'center'
                     }}>
                       <div style={{ flex: '1 1 300px' }}>
                         <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                           <strong>{r.name}</strong> <span style={{ color: '#7a8fa6', fontSize: '0.8rem' }}>• {r.role || 'No Role'}</span>
                           <span style={{ color: '#7a8fa6', fontSize: '0.8rem' }}>• {formatTimestamp(r.createdAt)}</span>
                           <span style={{ 
                             background: r.approved ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                             color: r.approved ? '#4ade80' : '#f87171',
                             padding: '2px 8px', borderRadius: 100, fontSize: '0.7rem', fontWeight: 700
                           }}>
                             {r.approved ? 'APPROVED' : 'PENDING'}
                           </span>
                         </div>
                         <div style={{ color: '#f5c518', fontSize: '0.9rem', marginBottom: 8 }}>
                           {Array.from({ length: r.stars || 5 }).map((_, j) => <i key={j} className="fas fa-star" />)}
                         </div>
                         <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem', margin: 0, lineHeight: 1.5 }}>"{r.text}"</p>
                       </div>
                       
                       <div style={{ display: 'flex', gap: 8 }}>
                         <button onClick={() => handleApprove(r.id, r.approved)} style={{ background: r.approved ? 'rgba(255,255,255,0.1)' : '#22c55e', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                           {r.approved ? 'Hide' : 'Approve'}
                         </button>
                         <button onClick={() => handleReviewDelete(r.id)} style={{ background: 'transparent', border: '1px solid rgba(239,68,68,0.5)', color: '#f87171', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                           <i className="fas fa-trash-alt" />
                         </button>
                       </div>
                     </div>
                   ))}
                 </div>
               )}
             </>
           ) : activeTab === 'projects' ? (
             <AdminProjects />
           ) : activeTab === 'properties' ? (
             <AdminProperties />
           ) : activeTab === 'updates' ? (
             <AdminUpdates />
           ) : activeTab === 'services' ? (
             <AdminServices />
           ) : (
             <>
               <h2 style={{ fontSize: '1.2rem', marginBottom: 20, color: '#7a8fa6' }}>Captured Leads ({leads.length})</h2>
               {leads.length === 0 ? (
                 <div style={{ background: 'rgba(255,255,255,0.03)', padding: 40, borderRadius: 12, textAlign: 'center', color: '#7a8fa6' }}>
                   <i className="fas fa-inbox fa-3x" style={{ opacity: 0.3, marginBottom: 16 }} />
                   <br/>No lead inquiries received yet.
                 </div>
               ) : (
                 <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                   {leads.map(l => (
                     <div key={l.id} style={{ 
                       background: 'rgba(255,255,255,0.04)', 
                       borderLeft: `4px solid #3b82f6`,
                       borderRadius: 8, padding: 20,
                       display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'space-between', alignItems: 'flex-start'
                     }}>
                       <div style={{ flex: '1 1 300px' }}>
                         <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', marginBottom: 12 }}>
                           <strong style={{ fontSize: '1.1rem' }}>{l.name}</strong>
                           <a href={`tel:${l.phone}`} style={{ color: '#60a5fa', textDecoration: 'none' }}><i className="fas fa-phone-alt"/> {l.phone}</a>
                           {l.email && <a href={`mailto:${l.email}`} style={{ color: '#60a5fa', textDecoration: 'none' }}><i className="fas fa-envelope"/> {l.email}</a>}
                           <span style={{ color: '#7a8fa6', fontSize: '0.85rem' }}><i className="far fa-clock"/> {formatTimestamp(l.createdAt)}</span>
                         </div>
                         <div style={{ background: 'rgba(255,255,255,0.05)', padding: '4px 10px', borderRadius: 4, display: 'inline-block', fontSize: '0.8rem', color: '#f5c518', marginBottom: 10 }}>
                           Service: {l.service || 'Not specified'}
                         </div>
                         <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem', margin: 0, lineHeight: 1.5, background: 'rgba(0,0,0,0.2)', padding: 12, borderRadius: 6 }}>
                           {l.message}
                         </p>
                       </div>
                       
                       <div>
                         <button onClick={() => handleLeadDelete(l.id)} style={{ background: 'transparent', border: '1px solid rgba(239,68,68,0.5)', color: '#f87171', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
                           <i className="fas fa-trash-alt" /> Delete
                         </button>
                       </div>
                     </div>
                   ))}
                 </div>
               )}
             </>
           )}
        </section>

      </div>
    </div>
  )
}
